<script setup lang="ts" generic="DataT, FooterDataT">
import { computed, markRaw, ref } from 'vue'
import orderBy from 'lodash/orderBy'
import get from 'lodash/get'
import DynamicHeading from '../DynamicHeading.vue'
import type { Table } from '../../../../types'
import { SortDirection } from '../../../enums/sort-direction'
import { compile } from '../../../support/compiler'
import LoadingCard from '../LoadingCard.vue'
import Head from './Head.vue'
import Cell from './Cell.vue'

interface Props {
  /**
   * Title of the table
   *
   * @type string
   * @default undefined
   */
  title?: string
  /**
   * Table Schema
   *
   * @type {Table.TableStructure}
   * @default undefined
   */
  schema: Table.TableStructure<DataT, FooterDataT>
  /**
   * Represents the primary dataset or information structure utilized within the application.
   * This variable is intended to hold data conforming to the `DataT` type.
   */
  data: DataT[]
  /**
   * Represents the data structure for the footer section of a user interface or webpage.
   * This data defines the content and layout elements displayed in the footer.
   * Only the object is allowed.
   */
  footerData?: FooterDataT
  /**
   * Indicates whether the associated data can be sorted.
   * A value of `true` allows for sorting functionality,
   * while `false` disables it. If undefined, the default
   * behavior may depend on the context of implementation.
   *
   * @type boolean
   * @default false
   */
  isSortable?: boolean
  /**
   * Represents the loading state of a process or operation.
   */
  isLoading?: boolean
  /**
   * Represents the default index to be used for sorting operations.
   * This value determines the initial order in which items are sorted.
   * If not defined, no default sorting index will be applied.
   *
   * @type number
   * @default undefined
   */
  defaultSortIndex?: number
  /**
   * Represents the default index of the row that determines the sorting order in a dataset.
   * Used for multi header rows arrays and usually not needed to be set.
   *
   * @type number
   * @default 0
   */
  defaultSortRowIndex?: number
  /**
   * Represents the default sorting direction to be applied when sorting data.
   * This may be used to determine whether the sort order should be ascending or descending.
   * It can be undefined if no default sorting direction is specified.
   *
   * @type {SortDirection | undefined}
   * @default undefined
   */
  defaultSortDirection?: SortDirection
  /**
   * Represents optional configuration settings for the table.
   *
   * @property {Partial<Table.TableOptions>} [options]
   * Defines partial table options to configure aspects such as layout, behavior, or appearance.
   */
  options?: Partial<Table.TableOptions>
  tokens?: Table.TableTokenCallback<DataT>
}

const defaults: Table.TableOptions = {
  classes: {
    titleClass: 'wmt-ui-table__title',
    tableWrapperClass: 'wmt-ui-table__wrapper',
    tableClass: 'wmt-ui-table',
    headClass: 'wmt-ui-table__head',
    headCellClass: 'wmt-ui-table__cell wmt-ui-table__cell--head',
    headCellSortableClass: 'wmt-ui-table__cell--sortable',
    headCellSortingClass: 'wmt-ui-table__cell--sorting',
    headCellWrapperClass: 'wmt-ui-table__head-cell-wrapper',
    headRowClass: 'wmt-ui-table__row wmt-ui-table__row--head',
    bodyClass: 'wmt-ui-table__body',
    bodyRowClass: 'wmt-ui-table__row wmt-ui-table__row--body',
    cellClass: 'wmt-ui-table__cell wmt-ui-table__cell--body',
    footerClass: 'wmt-ui-table__footer',
    footerCellClass: 'wmt-ui-table__cell wmt-ui-table__cell--footer',
    footerRowClass: 'wmt-ui-table__row wmt-ui-table__row--footer',
  },
  sortIcons: {
    default: 'fluent:arrow-sort-20-regular',
    asc: 'fluent:arrow-sort-up-lines-20-filled',
    desc: 'fluent:arrow-sort-down-lines-20-filled',
  },
  emptyText: 'No Data',
}

const props = withDefaults(defineProps<Props>(), {
  isSortable: false,
  isLoading: false,
  defaultSortIndex: undefined,
  defaultSortRowIndex: 0,
  defaultSortDirection: undefined,
  options: () => ({}),
})

const tableOptions = computed<Table.TableOptions>(() => Object.assign({}, defaults, props.options))
const noDataColspan = computed<number>(() => {
  return props.schema.head.rows.reduce((maxColspan, row) => {
    const rowColspan = row.cells.reduce((sum, cell) => sum + (cell?.colspan || 1), 0)
    return Math.max(maxColspan, rowColspan)
  }, 0)
})
const isTableSortable = computed<boolean>(() => !props.isLoading && props.isSortable)

const sortCellIndex = ref<number | undefined>(props.defaultSortIndex)
const sortRowIndex = ref<number | undefined>(props.defaultSortRowIndex)
const sortDirection = ref<SortDirection | undefined>(props.defaultSortDirection)

/**
 * A function to handle sorting logic for a table or grid component.
 * It updates the sorting index and direction based on the specified row and cell indexes and the desired sort direction.
 *
 * @param {object} params - The parameters for sorting.
 * @param {number} params.rowIndex - The index of the row to be used for sorting.
 * @param {number} params.cellIndex - The index of the cell to be used for sorting.
 * @param {SortDirection} [params.direction] - The desired direction for sorting (e.g., ascending or descending).
 */
const handleSort = ({ rowIndex, cellIndex, direction }: {
  rowIndex: number
  cellIndex: number
  direction?: SortDirection
}) => {
  if (sortRowIndex.value === rowIndex && sortCellIndex.value === cellIndex) {
    sortDirection.value = direction
  }
  else {
    sortDirection.value = SortDirection.Ascending
  }
  sortRowIndex.value = rowIndex
  sortCellIndex.value = cellIndex
}

/**
 * Calculates the effective position for a body cell by the colspan of header cells.
 *
 * @param {number} rowIndex - The index of the current body row.
 * @param {number} cellIndex - The index of the current body cell.
 * @returns {number} - The effective position for the specified body cell.
 */
const getEffectiveBodyCellPosition = (rowIndex: number, cellIndex: number) => {
  let effectivePosition = 0

  const currentRow = props.schema.head.rows[rowIndex]

  if (!currentRow) {
    return effectivePosition
  }

  for (let index = 0; index < cellIndex; index++) {
    const headerCell = currentRow.cells[index]

    effectivePosition += headerCell?.colspan || 1
  }

  return effectivePosition
}

/**
 * A computed property that generates an array of table body rows (`Table.BodyRow[]`) based on provided schema, data, and sorting properties.
 *
 * The computation involves:
 * - Mapping over the input data (`props.data`) to construct rows with cells.
 * - Using the row and cell schema definitions (`props.schema.body.rows.cells`) to determine the structure and content of each cell.
 * - Handling dynamic content generation using casting and function evaluation for cells.
 * - Applying sorting logic if sorting is enabled (`props.isSortable`), including support for a custom `onSort` method in cell schema.
 *
 * This property dynamically updates based on reactive dependencies, including schema, data, sorting index, and sorting direction.
 */
const generatedRows = computed<Table.BodyRow<DataT>[]>(() => {
  const cellsSchemas: Table.BodyCell<DataT>[] = markRaw(props.schema.body.rows.cells)

  const compiledRows = props.data.map((item, rowIndex) => {
    return {
      cells: cellsSchemas.map((cellSchema) => {
        const castedContent = cast(cellSchema.content) as Table.CellCastableContent<DataT>
        return {
          ...cellSchema,
          content: typeof castedContent === 'function' ? castedContent(item, rowIndex, cellSchema) : castedContent,
        }
      }),
      data: item,
    } as Table.BodyRow<DataT>
  })

  if (!sortDirection.value || sortCellIndex.value === undefined || !props.isSortable) {
    return compiledRows
  }

  const indexedCompiledRows: (Table.BodyRow<DataT> & {
    rowIndex: number
  })[] = compiledRows.map((row, index) => ({ ...row, rowIndex: index }))

  return orderBy(indexedCompiledRows, (row: Table.BodyRow<DataT> & { rowIndex: number }) => {
    const effectivePosition = getEffectiveBodyCellPosition(Number(sortRowIndex.value), Number(sortCellIndex.value))
    const column = row.cells[effectivePosition]
    const data = props.data[Number(row.rowIndex)]

    if (typeof column?.onSort === 'function' && sortCellIndex.value && sortDirection.value) {
      return column.onSort(sortCellIndex.value, column, sortDirection.value, row, data)
    }

    return column?.content || ''
  }, sortDirection.value) as Table.BodyRow<DataT>[]
})

/**
 * A computed property that generates an array of footer row objects for a table based on the provided schema and footer data.
 * Each footer row contains cells, and the content of each cell is either directly casted or computed dynamically using a function.
 *
 * The computation utilizes the `footer.rows` schema provided in the table's schema, and applies the `content` logic where
 * it evaluates the cell content or transforms it based on the `footerData` and row schema.
 */
const footerRows = computed<Table.FooterRow<FooterDataT, DataT>[]>(() => {
  if (!props.schema.footer) {
    return []
  }

  const footerSchemas: Table.FooterRow<FooterDataT, DataT>[] = markRaw(props.schema.footer.rows)

  return footerSchemas.map((rowSchema, rowIndex) => {
    return {
      cells: rowSchema.cells.map((cellSchema) => {
        const castedContent = cast(cellSchema.content) as Table.FooterCellCastableContent<FooterDataT, DataT>
        return {
          ...cellSchema,
          content: typeof castedContent === 'function' ? castedContent(props.footerData, props.data, rowIndex, cellSchema) : castedContent,
        }
      }),
    } as Table.FooterRow<FooterDataT, DataT>
  })
})

/**
 * Converts the provided value into a callable function based on its type.
 *
 * - If the value is a function, it is returned as-is.
 * - If the value is a string, it is transformed into a function that resolves
 *   tokens from provided data and compiles the result based on the string template.
 * - If the value is neither a function nor a string, it is returned as-is.
 *
 * @param value - The content to be cast, which may be a function,
 *                                            a string containing tokens, or another type.
 * @returns {(Function | *)} A callable function if the input is a function or a string.
 *                         Otherwise, returns the input value unchanged.
 */
const cast = (value: Table.CellCastableContent<DataT> | Table.FooterCellCastableContent<FooterDataT, DataT>) => {
  if (value && typeof value === 'function') {
    return value
  }
  else if (value && typeof value === 'string') {
    return (data: DataT, index: number) => {
      const dataTokens = resolveTokens(data, index)

      return compile(value as string).provide((req) => {
        return req.reduce(
          (tokens, token) => Object.assign(tokens, { [token]: () => dataTokens[token] }),
          {} as Record<string, () => unknown>,
        )
      })()
    }
  }

  return value
}

/**
 * Resolves token definitions based on the provided data and index.
 *
 * @param {Record<string, any>} data - The data object containing token values.
 * @param {number} index - The index representing positional data for token resolution.
 * @returns {Table.TableTokenDefinition} An object containing methods to access token values and retrieve the index.
 */
const resolveTokens = (data: DataT, index: number): Table.TableTokenDefinition => {
  return {
    // @ts-expect-error Need to resolve the type correctly
    get: (path: string, defaultValue: number | string | undefined = '') => get(data, path, defaultValue),
    index: () => index,
    ...(props?.tokens ? props.tokens(data, index) : {}),
  }
}

/**
 * Determines the appropriate classname for a table cell based on its provided configuration.
 *
 * @param {Table.BodyCell | Table.Cell} cell - The table cell object containing the classname or a function to generate it dynamically.
 * @param {number} rowIndex - The index of the row containing the cell.
 * @param {number} cellIndex - The index of the cell within the row.
 * @returns {string | string[] | Record<string, boolean>} The resolved classname for the cell, or undefined if no classname is defined.
 */
const resolveCellClassname = (cell: Table.BodyCell<DataT> | Table.Cell<DataT> | Table.FooterCell<FooterDataT, DataT>, rowIndex: number, cellIndex: number): undefined | Table.ClassName => {
  if (!cell?.className) {
    return undefined
  }
  else if (typeof cell.className === 'function') {
    return cell.className(rowIndex, cellIndex, cell)
  }

  return cell.className
}

/**
 * Resolves custom properties for a given table cell. This function determines the properties
 * based on the `props` attribute of the cell. If `props` is a function, it invokes the function
 * with the provided row index, cell index, and the cell itself to retrieve the properties.
 * If `props` is not defined or otherwise not a function, it directly returns `props`.
 *
 * @param {Table.BodyCell<DataT> | Table.Cell<DataT>} cell - The cell object for which custom properties are resolved.
 * @param {number} rowIndex - The index of the row to which the cell belongs.
 * @param {number} cellIndex - The index of the cell within the row.
 * @returns {Record<string, unknown>} The resolved custom properties for the cell.
 */
const resolveCellCustomProps = (cell: Table.BodyCell<DataT>, rowIndex: number, cellIndex: number): Record<string, unknown> => {
  const data = generatedRows.value?.[rowIndex]?.data || undefined

  const result: Record<string, unknown> = {
    data,
  }

  if (!cell?.props) {
    return result
  }
  else if (typeof cell?.props === 'function') {
    return {
      data,
      ...cell.props(data, rowIndex, cellIndex, cell),
    }
  }

  return {
    ...props,
    data,
  }
}
</script>

<template>
  <div>
    <DynamicHeading
      v-if="title"
      :class="tableOptions.classes.titleClass"
    >
      {{ title }}
    </DynamicHeading>
    <div :class="tableOptions.classes.tableWrapperClass">
      <table :class="tableOptions.classes.tableClass">
        <thead :class="tableOptions.classes.headClass">
          <tr
            v-for="(headRow, rowIndex) in schema.head.rows"
            :key="`head-row-${rowIndex}`"
            :class="tableOptions.classes.headRowClass"
          >
            <Head
              v-for="(cell, cellIndex) in headRow.cells"
              :key="`head-cell-${rowIndex}-${cellIndex}`"
              :cell="cell"
              :cell-index="cellIndex"
              :row-index="rowIndex"
              :sort-cell-index="sortCellIndex"
              :sort-row-index="sortRowIndex"
              :colspan="cell?.colspan"
              :rowspan="cell?.rowspan"
              :sort-direction="sortDirection"
              :sort-icons="tableOptions.sortIcons"
              :is-table-sortable="isTableSortable"
              :class-name="tableOptions.classes.headCellClass"
              :sortable-class-name="tableOptions.classes.headCellSortableClass"
              :sorting-class-name="tableOptions.classes.headCellSortingClass"
              :cell-classname="resolveCellClassname(cell, rowIndex, cellIndex)"
              :wrapper-classname="tableOptions.classes.headCellWrapperClass"
              @sort="handleSort"
            />
          </tr>
        </thead>
        <tbody :class="tableOptions.classes.bodyClass">
          <template v-if="generatedRows.length > 0">
            <tr
              v-for="(row, rowIndex) in generatedRows"
              :key="`table-body-row-${rowIndex}`"
              :class="tableOptions.classes.bodyRowClass"
            >
              <template
                v-for="(cell, cellIndex) in row.cells"
                :key="`table-cell-${rowIndex}-${cellIndex}`"
              >
                <component
                  :is="{ ...cell.component }"
                  v-if="cell?.component"
                  :key="`table-cell-${rowIndex}-${cellIndex}`"
                  :class="tableOptions.classes.cellClass"
                  :row="row"
                  :colspan="cell?.colspan"
                  :rowspan="cell?.rowspan"
                  :cell="cell"
                  :cell-classname="resolveCellClassname(cell, rowIndex, cellIndex)"
                  v-bind="resolveCellCustomProps(cell, rowIndex, cellIndex)"
                />
                <td
                  v-else
                  :class="tableOptions.classes.cellClass"
                  :colspan="cell?.colspan"
                  :rowspan="cell?.rowspan"
                  v-bind="resolveCellCustomProps(cell, rowIndex, cellIndex)"
                >
                  <span
                    v-if="cell?.allowHtml"
                    :class="resolveCellClassname(cell, rowIndex, cellIndex)"
                    v-html="cell.content"
                  />
                  <span
                    v-else
                    :class="resolveCellClassname(cell, rowIndex, cellIndex)"
                  >{{ cell.content }}</span>
                </td>
              </template>
            </tr>
          </template>
          <tr
            v-else-if="isLoading"
            :class="tableOptions.classes.bodyRowClass"
          >
            <Cell
              :class="tableOptions.classes.cellClass"
              :colspan="noDataColspan"
              :index="1"
              :cell="{ content: '' }"
            >
              <template #default="{ content, cellClassname }">
                <slot
                  name="loading"
                  :content="content"
                  :cell-classname="cellClassname"
                >
                  <LoadingCard :class="cellClassname" />
                </slot>
              </template>
            </Cell>
          </tr>
          <tr
            v-else
            :class="tableOptions.classes.bodyRowClass"
          >
            <Cell
              :class="tableOptions.classes.cellClass"
              :colspan="noDataColspan"
              :index="1"
              :cell="{ content: tableOptions.emptyText }"
            >
              <template #default="{ content, cellClassname }">
                <slot
                  name="empty"
                  :content="content"
                  :cell-classname="cellClassname"
                >
                  <span :class="cellClassname">{{ content }}</span>
                </slot>
              </template>
            </Cell>
          </tr>
        </tbody>
        <tfoot
          v-if="schema.footer?.rows && !isLoading"
          :class="tableOptions.classes.footerClass"
        >
          <tr
            v-for="(footerRow, rowIndex) in footerRows"
            :key="`footer-row-${rowIndex}`"
            :class="tableOptions.classes.footerRowClass"
          >
            <Cell
              v-for="(cell, cellIndex) in footerRow.cells"
              :key="`footer-cell-${rowIndex}-${cellIndex}`"
              :colspan="cell?.colspan"
              :rowspan="cell?.rowspan"
              :cell="cell"
              :index="cellIndex"
              :data="footerData"
              :class="tableOptions.classes.footerCellClass"
              :cell-classname="resolveCellClassname(cell, rowIndex, cellIndex)"
            />
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>
